/**
 * Success / failure toasts for ticket mutations.
 *
 * Wraps the design system toast manager so every write in this feature reports
 * the same way: a short confirmation on success, and on failure the translated
 * rendering of the API error code (never the server's English `message`, which
 * the contract marks as developer text).
 */

import { useToastManager } from "@norican/react";
import { useMemo } from "react";

import { useApiErrorMessage } from "@/components/common";
import { logger } from "@/lib/logger";

export interface FeedbackToast {
  success: (title: string, description?: string) => void;
  failure: (error: unknown, title?: string) => void;
}

export function useFeedbackToast(): FeedbackToast {
  const toast = useToastManager();
  const resolveError = useApiErrorMessage();

  return useMemo<FeedbackToast>(
    () => ({
      success: (title, description) => {
        toast.add({ title, description, type: "success" });
      },
      failure: (error, title) => {
        const resolved = resolveError(error);
        logger.debug("[tickets] mutation failed", resolved.traceId, error);
        toast.add({
          title: title ?? resolved.title,
          description: resolved.description,
          type: "danger",
          // A failed write must interrupt; a success can wait its turn.
          priority: "high",
        });
      },
    }),
    [resolveError, toast],
  );
}
